import React, { useState, useEffect } from 'react';
import DashboardSidebar, { INCOMING_TABS, ROLE_DEPARTMENT_VIEW } from './dashboard/DashboardSidebar';
import { useAuth } from '../../context/AuthContext';
import DashboardHeader from './dashboard/DashboardHeader';
import CockpitView from './dashboard/CockpitView';
import DepartmentViews from './dashboard/DepartmentViews';
import ProjectsAndApprovalsView from './dashboard/ProjectsAndApprovalsView';
import TechViews from './dashboard/TechViews';
import DashboardModals from './dashboard/DashboardModals';
import { dashboardService } from '../../services/dashboardService';
import '../../styles/executive-dashboard.css';

const TITLES = {
  'view-cockpit': { title: 'EXECUTIVE AGENTIC COCKPIT', sub: 'TECHMEDCOM — AI-POWERED DECISION CENTER' },
  'view-dept-sales': { title: 'DASHBOARD KINH DOANH & PHÂN PHỐI OTC', sub: 'THE THAO PHARMA & KÊNH NHÀ THUỐC TOÀN QUỐC' },
  'view-dept-mkt': { title: 'DASHBOARD PHÒNG MARKETING & TRUYỀN THÔNG', sub: 'THE THAO PHARMA & HỆ SINH THÁI TECHMEDCOM' },
  'view-dept-proc': { title: 'DASHBOARD THU MUA & CHUỖI CUNG ỨNG', sub: 'THE THAO PHARMA — QUẢN TRỊ NGUỒN HÀNG DƯỢC' },
  'view-dept-log': { title: 'DASHBOARD KHO VẬN & LOGISTICS DƯỢC PHẨM', sub: 'THE THAO PHARMA — QUẢN TRỊ 3 MIỀN & SLA GIAO HÀNG' },
  'view-dept-hr': { title: 'DASHBOARD HÀNH CHÍNH NHÂN SỰ & TUYỂN DỤNG', sub: 'THE THAO PHARMA — QUẢN TRỊ NGUỒN NHÂN LỰC' },
  'view-dept-acc': { title: 'DASHBOARD KẾ TOÁN & TÀI CHÍNH DOANH NGHIỆP', sub: 'THE THAO PHARMA — QUẢN TRỊ DÒNG TIỀN & CÔNG NỢ' },
  'view-dept-rnd': { title: 'DASHBOARD R&D & SẢN PHẨM TECHPHA', sub: 'THE ONE PHARMA — NGHIÊN CỨU & CÔNG BỐ SẢN PHẨM MỚI' },
  'view-dept-assist': { title: 'DASHBOARD BAN TRỢ LÝ', sub: 'THE THAO PHARMA — HỖ TRỢ ĐIỀU HÀNH & VĂN PHÒNG' },
  'view-projects': { title: 'QUẢN TRỊ DỰ ÁN TRỌNG ĐIỂM', sub: 'TIẾN ĐỘ 4 DỰ ÁN: TTP, TOP, TTCE, TECHPHA' },
  'view-approvals': { title: 'HÀNG ĐỢI PHÊ DUYỆT (BOD)', sub: 'PHÊ DUYỆT 1-CLICK CẤP BAN LÃNH ĐẠO' },
  'view-risks': { title: 'PHỤ THUỘC & RỦI RO', sub: 'MA TRẬN SLA LIÊN PHÒNG BAN & ĐIỂM NGHẼN' },
  'view-agents': { title: 'GIÁM SÁT BOT & SKILLS', sub: 'HIỆU SUẤT ĐỘI NGŨ AI AGENT & THƯ VIỆN KỸ NĂNG' },
  'view-datasources': { title: 'CSDL & PHÂN QUYỀN RBAC', sub: 'KẾT NỐI CSDL CHOTHUOC247.VN & PHÂN QUYỀN' },
  'view-outputs': { title: 'KHO LƯU TRỮ ĐẦU RA', sub: 'QUẢN TRỊ OUTPUT VAULT & MÃ TRUY VẾT' },
  'view-audit': { title: 'AUDIT TRAIL & ROI SIMULATOR', sub: 'NHẬT KÝ HỆ THỐNG & TÍNH TOÁN HIỆU QUẢ KINH TẾ' }
};

export default function ExecutiveDashboard() {
  const { user } = useAuth();
  // Trưởng phòng (role khớp với 1 phòng ban) chỉ được xem đúng dashboard phòng mình;
  // admin (hoặc role không nằm trong map) xem toàn bộ, bắt đầu ở Cockpit như cũ.
  const restrictedViewId = user?.role ? ROLE_DEPARTMENT_VIEW[user.role] : undefined;

  const [activeView, setActiveView] = useState(restrictedViewId || 'view-cockpit');
  const [openModalId, setOpenModalId] = useState(null);
  const [toastMsg, setToastMsg] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [troLyData, setTroLyData] = useState(null);

  useEffect(() => {
    const originalTitle = document.title;
    document.title = 'TECHMEDCOM AI — EXECUTIVE AGENTIC COCKPIT & PHÒNG BAN';
    document.body.classList.add('exec-cockpit-body');

    // Fetch dashboard data
    dashboardService.getSummary()
      .then((data) => {
        if (data) {
          setApiData(data);
          console.log('✅ Dashboard React đã kết nối dữ liệu API thành công:', data);
        }
      })
      .catch((err) => {
        console.log('Dashboard API sync status:', err.message);
      });

    // Fetch dữ liệu dashboard Ban Trợ Lý (Trợ Lý Công Nghệ)
    dashboardService.getTroLyDashboard()
      .then((data) => {
        if (data) setTroLyData(data);
      })
      .catch((err) => {
        console.log('Ban Trợ Lý dashboard sync status:', err.message);
      });

    return () => {
      document.title = originalTitle;
      document.body.classList.remove('exec-cockpit-body');
    };
  }, []);

  const handleShowToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => {
      setToastMsg(null);
    }, 3000);
  };

  const handleNavigate = (viewId) => {
    if (restrictedViewId && viewId !== restrictedViewId) return;
    setActiveView(viewId);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const viewName = TITLES[viewId]?.title || viewId;
    handleShowToast(`Đã chuyển đến: ${viewName}`);
  };

  const handleOpenModal = (modalId) => {
    setOpenModalId(modalId);
  };

  const handleCloseModal = () => {
    setOpenModalId(null);
  };

  const handleSubmitNewTask = (taskTitle) => {
    handleShowToast(`Đã phân bổ task thành công cho Agent: "${taskTitle}"`);
  };

  const currentTitleInfo = TITLES[activeView] || TITLES['view-cockpit'];

  return (
    <div className="exec-cockpit-layout">
      {/* 1. Sidebar */}
      <DashboardSidebar
        activeView={activeView}
        onNavigate={handleNavigate}
        onOpenModal={handleOpenModal}
        onShowToast={handleShowToast}
        restrictedViewId={restrictedViewId}
      />

      {/* 2. Main Cockpit Content Area */}
      <main className="main-cockpit">
        <DashboardHeader
          title={currentTitleInfo.title}
          subtitle={currentTitleInfo.sub}
          isIncoming={Boolean(INCOMING_TABS[activeView])}
          onOpenModal={handleOpenModal}
          onShowToast={handleShowToast}
        />

        {/* View 01: Cockpit Overview */}
        <CockpitView
          activeView={activeView}
          onNavigate={handleNavigate}
          onOpenModal={handleOpenModal}
          onShowToast={handleShowToast}
          apiData={apiData}
          troLyData={troLyData}
        />

        {/* Views: 8 Department Views (Sales, Marketing, Procure, Logistics, HR, Accounting, R&D, Ban Trợ Lý) */}
        <DepartmentViews
          activeView={activeView}
          onNavigate={handleNavigate}
          onOpenModal={handleOpenModal}
          onShowToast={handleShowToast}
          apiData={apiData}
          troLyData={troLyData}
        />

        {/* Views: Projects, Approvals, Risks */}
        <ProjectsAndApprovalsView
          activeView={activeView}
          onNavigate={handleNavigate}
          onOpenModal={handleOpenModal}
          onShowToast={handleShowToast}
          apiData={apiData}
        />

        {/* Views: Agents, Datasources, Outputs, Audit */}
        <TechViews
          activeView={activeView}
          onNavigate={handleNavigate}
          onOpenModal={handleOpenModal}
          onShowToast={handleShowToast}
          apiData={apiData}
        />
      </main>

      {/* 3. Modals & Toast */}
      <DashboardModals
        openModalId={openModalId}
        onCloseModal={handleCloseModal}
        onShowToast={handleShowToast}
        onSubmitNewTask={handleSubmitNewTask}
        toastMsg={toastMsg}
      />
    </div>
  );
}
